/**
 * Common Component JavaScript
 *
 * Shared behaviors for templates used across dropsite flows.
 * Each behavior is tied to a data-* attribute declared in its template.
 */

import { Events } from '../js/utils/events.js';

let initialized = false;
let turnstileLoadPromise = null;

// ============================================================================
// Cloudflare Turnstile (every guest-facing form fragment)
// Loads Cloudflare's api.js on first use, then explicit-renders one widget
// per host and enables the form's submit button on a solved challenge.
// Template contract:
//   <div class="cf-turnstile-host" data-sitekey="..." data-submit="<submit id>">
//     <p data-turnstile-fallback>...</p>   {# optional, created here when absent #}
//   </div>
// ============================================================================

function loadTurnstile() {
    if (turnstileLoadPromise) return turnstileLoadPromise;
    if (window.turnstile && window.turnstile.render) {
        turnstileLoadPromise = Promise.resolve();
        return turnstileLoadPromise;
    }
    turnstileLoadPromise = new Promise((resolve, reject) => {
        window.__dropsiteTurnstileLoad = function () { resolve(); };
        const script = document.createElement('script');
        script.src =
            'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__dropsiteTurnstileLoad&render=explicit';
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Failed to load Cloudflare Turnstile'));
        document.head.appendChild(script);
    });
    return turnstileLoadPromise;
}

function renderTurnstileInto(host) {
    if (host.dataset.rendered === 'true') return;
    host.dataset.rendered = 'true';
    const submit = host.dataset.submit
        ? document.getElementById(host.dataset.submit)
        : null;
    if (!host.querySelector('[data-turnstile-fallback]')) {
        const placeholder = document.createElement('p');
        placeholder.setAttribute('data-turnstile-fallback', '');
        placeholder.className = 'text-sm text-gray-600';
        placeholder.textContent = 'Verifying you are human…';
        host.appendChild(placeholder);
    }
    let loaded = false;
    // A rendered widget replaces the host's children, taking the notice with it.
    const showBlocked = () => {
        const notice = host.querySelector('[data-turnstile-fallback]');
        if (!notice) return;
        notice.setAttribute('role', 'alert');
        notice.textContent =
            "Couldn't load the human-verification challenge from challenges.cloudflare.com. " +
            'If your network or a browser extension blocks that site, try another network ' +
            'or ask your IT team to allow it, then reload.';
    };
    loadTurnstile()
        .then(() => {
            loaded = true;
            window.turnstile.render(host, {
                sitekey: host.dataset.sitekey || '',
                callback: () => { if (submit) submit.disabled = false; },
                'expired-callback': () => { if (submit) submit.disabled = true; },
                'error-callback': () => { if (submit) submit.disabled = true; },
            });
        })
        .catch((err) => {
            console.error('[dropsite] Turnstile failed to load:', err);
            showBlocked();
        });
    // A proxy that hangs or answers with its own page never settles the load.
    setTimeout(() => { if (!loaded) showBlocked(); }, 20000);
}

// ============================================================================
// Timezone Offset Inputs (login and download authentication forms)
// The two flows disagree on sign: the download forms post the raw
// getTimezoneOffset(), the guest and portal login forms post it negated.
// Each input carries its own sign so both keep posting what they post today.
// Template contract: <input type="hidden" data-tz-sign="1|-1">
// ============================================================================

function applyTimezoneOffset(input) {
    input.value = Number(input.dataset.tzSign) * new Date().getTimezoneOffset();
}

// ============================================================================
// Navigation (Cancel / Back / "start over" buttons across every flow)
// Template contract:
//   <button data-nav="/drop/somewhere">   — go to that URL
//   <button data-nav-back>                — go back one history entry
// ============================================================================

function initNavigation() {
    Events.on('click', 'nav', (event, element) => {
        event.preventDefault();
        window.location.href = element.dataset.nav;
    });
    Events.on('click', 'nav-back', (event) => {
        event.preventDefault();
        window.history.back();
    });
}

// ============================================================================
// Form Field Shortcuts
// Clearing runs in the capture phase, so a button that also carries hx-post
// sends the cleared value rather than the stale one.
// Template contract:
//   <button data-clear-field="<field name>">  — blank that field on click
//   <select data-submit-on-change>            — submit the form on change
//   <textarea data-submit-on-enter>           — submit on Enter, newline on Shift+Enter
// ============================================================================

function initFieldShortcuts() {
    Events.on('click', 'clear-field', (event, element) => {
        const field = element.form && element.form.elements[element.dataset.clearField];
        if (field) field.value = '';
    });
    Events.on('change', 'submit-on-change', (event, element) => {
        if (element.form) element.form.requestSubmit();
    });
    Events.on('keydown', 'submit-on-enter', (event, element) => {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
        if (!element.form) return;
        event.preventDefault();
        element.form.requestSubmit();
    });
}

// ============================================================================
// Clickable Rows (portal start, explorer, quicksearch, inbox)
// The whole row navigates, but the controls inside it must not — a checkbox
// there toggles and a link goes to its own href. The row names what to skip,
// so those controls no longer each need a stopPropagation of their own.
// Template contract:
//   <tr data-row-href="/drop/..." data-row-ignore="input,a,button">
// ============================================================================

function initClickableRows() {
    Events.on('click', 'row-href', (event, row) => {
        const ignore = row.dataset.rowIgnore;
        if (ignore && event.target.closest(ignore)) return;
        window.location.href = row.dataset.rowHref;
    });
}

// ============================================================================
// Password Reveal (security-question answer field)
// Addressed by field name off the button's form, not by id: the input macro
// emits its own id from the name, so an id passed through attrs is a duplicate
// the parser drops — which is why this button did nothing for so long.
// Template contract:
//   <button data-password-toggle="<field name>" aria-pressed="false">Show</button>
// ============================================================================

function initPasswordToggle() {
    Events.on('click', 'password-toggle', (event, element) => {
        event.preventDefault();
        const field = element.form && element.form.elements[element.dataset.passwordToggle];
        if (!field) return;
        const revealing = field.type === 'password';
        field.type = revealing ? 'text' : 'password';
        element.textContent = revealing ? 'Hide' : 'Show';
        element.setAttribute('aria-pressed', revealing ? 'true' : 'false');
    });
}

// ============================================================================
// Public init
// ============================================================================

function activate(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.cf-turnstile-host').forEach(renderTurnstileInto);
    root.querySelectorAll('[data-tz-sign]').forEach(applyTimezoneOffset);
}

export function initCommon() {
    if (initialized) return;
    initialized = true;

    initNavigation();
    initFieldShortcuts();
    initClickableRows();
    initPasswordToggle();

    activate(document);

    // Form fragments self-replace on validation failure, bringing in a fresh
    // widget host and timezone input that need activating again. The event
    // fires on the swapped-in element, so scope off event.target —
    // detail.target is the replaced element, already out of the document.
    document.body.addEventListener('htmx:afterSwap', (event) => {
        activate(event.target);
    });
}
